<template>
  <article class="p-accommodation">
    <div class="p-accommodation__header js-scroll-animation-unit" ref="header">
      <div class="p-accommodation__eyecatch">
        <color-overlay class="p-accommodation__image" :offset="-1">
          <img :src="post.acf.main_image.url" :alt="post.acf.main_image.alt" class="u-cover">
        </color-overlay>  
      </div>
      

      <div class="p-accommodation__title">
        <h2 class="p-accommodation__heading"><span class="u-mask" v-text="post.acf.acc_name"></span></h2>
        <em class="p-accommodation__prefecture"><span class="u-mask" v-text="post.acf.acc_dest.post_title"></span></em>
        <p class="p-accommodation__lead u-fade" v-html="post.acf.acc_lead"></p>
      </div>
    </div>
    
    <scroll-spy ref="scrollSpy">
      <!-- wysiwyg -->
      <scroll-spy-section v-if="post.acf.modules.length" :label="`Story`">
        <div class="p-container">
          <section class="p-accommodation__content">
            <global-wysiwyg :post="post" />
          </section>
        </div>
      </scroll-spy-section>
      <!-- /wysiwyg -->
      
      <!-- feature -->
      <scroll-spy-section v-if="post.acf.acc_feature" :label="`Feature`">
        <div class="p-accommodation__feature">
          <scroll-animation-unit :offset="0.8" class="p-accommodation__background"></scroll-animation-unit>

          <accommodation-feature 
            v-for="(feature, i) in post.acf.acc_feature" 
            :key="feature.title" 
            :flipped="i % 2 !== 0" 
            :image="feature.image" 
            :title="feature.title" 
            :text="feature.text" 
          />

          <!-- carousel -->
          <accommodation-carousel :gallery="post.acf.acc_feature_gallery" />
          <!-- /carousel -->
        </div>

        <div class="p-section">
          <!-- book now -->
          <book-button 
            v-if="post.acf.acc_booknow_url" 
            :url="post.acf.acc_booknow_url" 
            :eventLabel="booknowEventLabel"
            class="p-accommodation__bookButton">
            <template v-if="post.acf.acc_booknow_text">{{ post.acf.acc_booknow_text }}</template>
            <template v-else>Book Now</template>
          </book-button>
          <!-- /book now -->

          <!-- Other Accommodation -->
          <aside class="p-otherAccommodation">
            <h2 class="p-otherAccommodation__heading">Other Accommodation</h2>

            <other-accommodation :accommodations="accommodations" :post-id="getInfo().the_id" />
          </aside>
          <!-- /Other Accommodation -->
        </div>
      </scroll-spy-section>
      <!-- /feature -->
      
      <!-- Staff -->
      <scroll-spy-section v-if="post.acf.acc_members_main_image.url" :label="`Staff`">
        <div class="p-container">
          <cominca-members
            :main-image="post.acf.acc_members_main_image.url"
            :lead="post.acf.acc_members_lead" 
            :members="post.acf.acc_members"
            class="p-accommodation__members" 
          />
        </div>
      </scroll-spy-section>
      <!-- /Staff -->

      <!-- pickup -->
      <scroll-spy-section v-if="parentDestination.acf && parentDestination.acf.pickup" :label="`Explore`">
        <section class="p-section">
          <scroll-animation-unit>
            <h2 class="c-heading u-fade">Explore {{ capitalizedDestName }}</h2>
          </scroll-animation-unit>
          <cominca-pickup :items="parentDestination.acf && parentDestination.acf.pickup" />
        </section>
      </scroll-spy-section>
      <!-- pickup -->

      <!-- Access -->
      <scroll-spy-section v-if="post.acf.acc_gmap" :label="`Access`">
        <div class="p-container">
          <section class="p-section">
            <scroll-animation-unit>
              <h2 class="c-heading u-fade">Access</h2>
            </scroll-animation-unit>
            <cominca-access 
              :has-description="true" 
              :gmap="post.acf.acc_gmap" 
              :url="post.acf.acc_url" />
          </section>
        </div>
      </scroll-spy-section>
      <!-- /Access -->
    </scroll-spy>

    

    <!-- back -->
    <div class="p-buttonContainer" data-align="center">
      <transition-button :to="`/destination/${post.acf.acc_dest.post_name}`" type="back">Back to {{ capitalizedDestName }} TOP</transition-button>
    </div>
    <!-- /back -->

    <page-navigation :sections="sections" />
  </article>
</template>

<script>
  /**
   * Modules
   */
  import { mapGetters, mapActions, mapMutations } from 'vuex'
  import store from '~/store'
  import globalWysiwyg from '~/partial/global-wysiwyg'
  import TransitionButton from '~/partial/transition-button'
  import AccommodationFeature from '~/partial/accommodation-feature'
  import ComincaMembers from '~/partial/cominca-members'
  import ComincaAccess from '~/partial/cominca-access'
  import ComincaPickup from '~/partial/cominca-pickup'
  import BookButton from '~/partial/book-button'
  import PageNavigation from '~/partial/page-navigation'
  import AccommodationCarousel from '~/partial/accommodation-carousel'
  import OtherAccommodation from '~/partial/other-accommodation'

  import objectFitCover from '~/mixins/object-fit-cover'
  import imageLoader from '~/mixins/image-loader'

  /**
   * Share Partial
   */

  export default {
    name: 'page-acc-template',
    mixins: [
      objectFitCover,
      imageLoader,
    ],
    components: {
      globalWysiwyg,
      TransitionButton,
      AccommodationFeature,
      ComincaMembers,
      ComincaAccess,
      ComincaPickup,
      BookButton,
      PageNavigation,
      AccommodationCarousel,
      OtherAccommodation,
    },
    data () {
      return {
        type: '',
        post: {},
        model: {
          title: ''
        },
        sections: [],
        parentDestination: {},
        accommodations: [],
        the_id: '',
        booknowEventLabel: ''
      }
    },
    computed: {
      capitalizedDestName() {
        let destName = this.post.acf.acc_dest.post_name
        return destName.charAt(0).toUpperCase() + destName.slice(1);
      },
      ...mapGetters({
        'getPost': 'posts/getPost',
        'getPosts': 'posts/getPosts',
        'getHeaders': 'posts/getHeaders',
        'getInfo': 'route/getInfo',
      })
    },
    created() {
      this.routing(this.getInfo());
      this.the_id = this.getInfo().the_id
      this.booknowEventLabel = this.post.acf.acc_name + '-homeaway';
    },
    async beforeRouteUpdate(to, from, next) {
      if(this.the_id !== this.getInfo().the_id) {
        let type = 'accommodation';
        let id = this.getInfo().the_id;
        await this.fetchPosts({ type, id });
      }

      // this.routing(this.getInfo());

      next()
    },
    beforeRouteLeave(to, from, next) {
      this.showLoader()

      next()
    },
    mounted() {
    },
    methods: {
      async update(){
        const option = {
          type: 'acc-by-dest',
          id: this.post.acf.acc_dest.ID 
        };

        await store.dispatch('posts/fetchPosts', option);

        // console.log('ここです');
        // console.log(this.getPosts('accommodation'));

        this.accommodations = this.getPosts('accommodation')

        // 親のdestinationを取得
        this.getDestination(this.post.acf.acc_dest.ID)

        
      },
      async routing(status={}) {
        this.type = 'accommodation';
        this.model.title = 'ACCOMMODATION';
        this.post = this.getPost('accommodation');
        // console.log(this.post)
        await this.update();
      },
      async getDestination(id) {
        if(!this.getPost('destination').acf) {
          let option = {
            type: 'destination',
            id
          }

          await store.dispatch('posts/fetchPosts', option)
        }

        this.parentDestination = this.getPost('destination')

        this.$_imageLoader_load(this.$el).then(this.onImagesLoaded)  
        
      },
      onImagesLoaded() {
        this.$nextTick(() => {
          this.$refs.scrollSpy.watch()

          this.sections = this.$refs.scrollSpy.getLabels()

          this.hideLoader()

          this.$refs.header.classList.add('is-animated')  
        })
        
      },
      ...mapActions({
        'fetchPosts': 'posts/fetchPosts',
      }),
      ...mapMutations({
        'showLoader': 'loader/SHOW',
        'hideLoader': 'loader/HIDE',
      })
    }
  }
</script>



// WEBPACK FOOTER //
// src/pages/acc_template.vue